import { ALREADY_PURCHASED_ERROR, PurchasedItemConflict } from "../shared/api-types/payment";

import request from "./request";

export type Bundle = {
  courses: { id: number; shortname: string }[];
  description: string;
  id: number;
  name: string;
  price: number;
  slots: string;
  trainingStatus: string;
};

type Products = {
  bundles: Bundle[];
  courses: { id: number; name: string; price: number; slots: string; trainingStatus: string; type: string }[];
};

const cid = (c: any) => c.courseId;

export const calculatePrice = async (items: any[], selectedBillingId: any) => {
  const courses = items.filter((c) => !c.isBundle).map(cid);
  const bundles = items.filter((c) => c.isBundle).map(cid);
  const users = items.map((u) => u.userId);

  const queryParams = [
    ...courses.map((cid) => `courses[]=${cid}`),
    ...bundles.map((bid) => `bundles[]=${bid}`),
    ...users.map((uid) => `u[]=${uid}`),
    `billingId=${selectedBillingId}`,
  ].join("&");
  const json = await request(`/payment/price?${queryParams}`);
  return json;
};

export const createOrder = async (items: any[], billingId: any) => {
  const json = await request.post(`/payment/order`, {
    items,
    billingId,
  });
  return json;
};

export const payWithStripe = async (
  token: any,
  priceForStripe: any,
  currency: any,
  items: any[],
  billingId: any,
  shoppingItems: any[],
  paymentType: any,
) => {
  const resp = await request.post(`/payment/stripe`, {
    priceForStripe,
    currency,
    token,
    items,
    billingId,
    shoppingItems,
    paymentType,
  });
  return resp;
};

export const createPayWithInvoice = async (
  items: any[],
  billingId: any,
  shoppingItems: any[],
  paymentType: any,
  status: any,
) => {
  const json = await request.post(`/payment/invoice`, {
    items,
    billingId,
    shoppingItems,
    status,
    paymentType,
    invoiceQR: "",
  });
  return json;
};

/**
 * Reads the duplicate-purchase conflicts out of a rejected checkout call.
 * Returns null when the error is not the guard's 409 (CREW-559).
 */
export const getAlreadyPurchasedConflicts = async (error: any): Promise<PurchasedItemConflict[] | null> => {
  if (error?.status !== 409 || typeof error.json !== "function") {
    return null;
  }
  try {
    const body = await error.json();
    if (body?.message === ALREADY_PURCHASED_ERROR && Array.isArray(body.conflicts)) {
      return body.conflicts;
    }
  } catch {
    // some other 409 - let the caller's generic error handling deal with it
  }
  return null;
};

export const updateOrder = async (orderId: any, items: any[]) => {
  const json = await request.put(`/payment/order/${orderId}`, items);
  return json;
};

export const deleteOrder = async (orderId: any) => {
  const status = await request.delete(`/payment/order/${orderId}`);
  return status;
};

export const getOrderStatus = async (orderId: any) => {
  const json = await request(`/payment/order/${orderId}`);
  return json;
};

export const getAvailableProducts = async (crewId: any, userId: any): Promise<Products> => {
  const json = await request(`/payment/products?crewId=${crewId}&userId=${userId}`);
  return json;
};

export const getUserBoughtCourses = async (userId: any, crewId: any) => {
  const json = await request(`/payment/products/${userId}?crewId=${crewId}`);
  return json;
};

export const getActiveSubscriptions = async (crewId: any) => {
  const url = crewId ? `/payment/subscriptions?crewId=${crewId}` : `/payment/subscriptions/all`;
  return await request(url);
};

export const cancelSubscription = async (subId: any, bundleId: any, userId: any) => {
  const json = await request.delete(`/payment/subscriptions/${subId}?bundleId=${bundleId}&userId=${userId}`);
  return json;
};
